import {useEffect, useState} from 'react';

import posthog from 'posthog-js';

interface PostHogABTestOptions<TVariant extends string> {
  featureFlagKey: string;
  defaultVariant?: TVariant | boolean;
}

interface PostHogABTestReturn<TVariant extends string> {
  variant: TVariant | boolean | undefined;
  isLoading: boolean;
}

export const usePosthogABTest = <TVariant extends string>({
  featureFlagKey,
  defaultVariant,
}: PostHogABTestOptions<TVariant>): PostHogABTestReturn<TVariant> => {
  const [variant, setVariant] = useState<TVariant | boolean | undefined>(defaultVariant);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    const handleFeatureFlagsReady = () => {
      if (!isMounted) {
        return;
      }

      const flagValue = posthog.getFeatureFlag(featureFlagKey) as TVariant | boolean | undefined;
      setVariant(flagValue ?? defaultVariant);
      setIsLoading(false);
    };

    if (!posthog.__loaded) {
      setVariant(defaultVariant);
      setIsLoading(false);
      return;
    }

    const unsubscribe = posthog.onFeatureFlags(handleFeatureFlagsReady);
    handleFeatureFlagsReady();

    return () => {
      isMounted = false;
      unsubscribe?.();
    };
  }, [defaultVariant, featureFlagKey]);

  return {
    variant,
    isLoading,
  };
};
